import { MouseEvent, useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { clsx } from 'clsx';
import Image from 'next/image';
import { EffectFade } from 'swiper/modules';
import { Swiper as SwiperApi } from 'swiper/types';

import { PRODUCT_IMAGE_PATH } from '@shared/config/paths/paths';
import { ProductPropertyValueType } from '@shared/types/product';

import cls from './Slider.module.scss';

type SliderProps = {
  hovered: boolean;
  slides: ProductPropertyValueType[];
  variant: 'row' | 'card';
  productName: string;
  className?: string;
  swiperClassName?: string;
  controlsClassName?: string;
  dotClassName?: string;
  dotActiveClassName?: string;
  advantagesClassName?: string;
  advantages?: string;
};

export const Slider = ({
  className,
  hovered,
  slides,
  swiperClassName,
  controlsClassName,
  dotClassName,
  dotActiveClassName,
  advantagesClassName,
  variant = 'card',
  productName,
  advantages
}: SliderProps) => {
  const [swiper, setSwiper] = useState<SwiperApi | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number | undefined>(0);

  const activeSlideHandler = useCallback((index: number | undefined) => {
    if (typeof index !== 'undefined') {
      setActiveSlideIndex(index);
    }
  }, []);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (swiper) {
      const containerWidth = e.currentTarget.offsetWidth;
      const mouseX = e.clientX - e.currentTarget.getBoundingClientRect().left;
      const slideCount = swiper.slides.length;
      const newSlideIndex = Math.floor((mouseX / containerWidth) * slideCount);
      const newActiveSlideIndex = newSlideIndex >= slideCount ? slideCount - 1 : newSlideIndex;
      swiper.slideTo(newActiveSlideIndex);
    }
  };

  const resetActiveSlide = () => {
    swiper?.slideTo(0);
    setActiveSlideIndex(0);
  };

  return (
    <div
      onMouseLeave={resetActiveSlide}
      onMouseMove={handleMouseMove}
      data-testid={'product-card-slider'}
      className={clsx(cls.wrap, className, { [cls.hover]: hovered, [cls.row]: variant === 'row' })}
    >
      <div className={cls.slidersWrap}>
        <Swiper
          fadeEffect={{
            crossFade: false
          }}
          loop
          slidesPerView={1}
          onSlideChange={() => activeSlideHandler(swiper?.realIndex)}
          onSwiper={(api: SwiperApi) => setSwiper(api)}
          effect="fade"
          modules={[EffectFade]}
          className={clsx(cls.slider, swiperClassName)}
        >
          {slides.map((slide, index) => (
            <SwiperSlide
              key={index}
              className={clsx(cls.slide)}
            >
              <Image
                src={`${PRODUCT_IMAGE_PATH}/${slide.value}/800.webp`}
                alt={`${productName}-${index}`}
                fill
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {advantages && <div className={clsx(advantagesClassName, cls.advantages)}>{advantages}</div>}
      </div>
      {slides && slides.length > 1 && (
        <div className={clsx(cls.controls, controlsClassName)}>
          {slides.map((_, index) => (
            <div
              className={clsx(cls.dot, dotClassName, {
                [clsx(cls.active, dotActiveClassName)]: activeSlideIndex === index
              })}
              key={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};
